import { Box, Flex, Stack } from "@chakra-ui/react";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { FieldValues, UseFormWatch } from "react-hook-form";
import { Toast } from "../../../components/atom/Toast/Toast";
import InputFieldComponent from "../../../components/molecule/InputField/InputFieldComponent";
import ModalComponent from "../../../components/organism/Modal/ModalComponent";
import ModalFooterComponent from "../../../components/organism/Modal/ModalFooterComponent";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { DOCUMENTS_URL } from "../../../utils/endpoints";
import { uploadFile } from "../../../helpers/uploadFile";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import DatePickerComponent from "../../../components/molecule/DatePicker/DatePickerComponent";
import dayjs, { Dayjs } from "dayjs";
import FileUpload from "../../../components/molecule/FileUpload/FileUpload";
import LabelComponent from "../../../components/atom/Label/LabelComponent";
import api from "../../../utils/api";
import { AuthUser } from "../../../types/User/AuthUser";
import RadioControlGroup from "../../../components/molecule/RadioControlGroup/RadioControlGroup";
import { Option } from "../../../types/General/Option";
import CheckboxControlGroup from "../../../components/molecule/CheckboxControlGroup/CheckboxControlGroup";
import { getSubSystem } from "../../../services/Indicator/getSubSystem";

interface ModalDocumentsProps {
  idItem?: number;
  errors: any;
  handleSubmit: any;
  control: any;
  modalConfig: ModalConfig;
  watch: UseFormWatch<FieldValues>;
  selectedYear: Dayjs | null;
  setSelectedYear: Dispatch<SetStateAction<Dayjs | null>>;
  uploadedFile: File | null | undefined;
  setUploadedFile: Dispatch<SetStateAction<File | null | undefined>>;
  setShouldShowFileError: Dispatch<SetStateAction<boolean>>;
  shouldShowFileError: boolean;
  user: AuthUser;
  loadingFile: boolean;
}

const ModalDocuments: FC<ModalDocumentsProps> = ({
  idItem,
  errors,
  handleSubmit,
  control,
  modalConfig,
  watch,
  selectedYear,
  setSelectedYear,
  uploadedFile,
  setUploadedFile,
  setShouldShowFileError,
  shouldShowFileError,
  user,
  loadingFile,
}) => {
  const { isOpen, title = "", buttonTitle, closeModal } = modalConfig;
  const [isLoaded, setIsLoaded] = useState(true);
  const { addToast } = Toast();
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const onSubmit = async (data: FieldValues) => {
    if (uploadedFile) {
      setIsLoaded(false);
      setShouldShowFileError(false);
      const formData = new FormData();

      // Include the id if available
      if (idItem && idItem !== 0) {
        formData.append("id", idItem.toString());
      }

      formData.append("Name", data.name);
      formData.append("AuthorName", data.authorName);
      formData.append(
        "PublishedAt",
        (selectedYear?.year() ?? data.publishedAt.year()).toString(),
      );
      formData.append("IsPrivate", data.permission.id === 1 ? "true" : "false");

      if (data.provinceOptions && data.provinceOptions.length > 0) {
        data.provinceOptions.forEach((option: Option) => {
          formData.append("ProvincesIds", option.id.toString());
        });
      }

      if (data.subSystemOptions && data.subSystemOptions.length > 0) {
        data.subSystemOptions.forEach((option: Option) => {
          formData.append("SubSystemsIds", option.id.toString());
        });
      }

      const response = await uploadFile(
        idItem && idItem !== 0 ? api.put : api.post,
        uploadedFile,
        idItem && idItem !== 0 ? `${DOCUMENTS_URL}/${idItem}` : DOCUMENTS_URL,
        "application/pdf",
        formData,
      );
      if (response) {
        setIsLoaded(true);
        addToast({
          status: "success",
          title: "Sucesso",
          description:
            idItem === 0 ? "Documento criado" : "Alterações Guardadas",
        });
      } else {
        setIsLoaded(true);
        addToast({
          status: "error",
          title: "Erro",
          description: `Ocorreu um erro ao ${
            idItem === 0 ? "criar" : "alterar"
          } o item`,
        });
      }

      closeModal();
    } else {
      setShouldShowFileError(true);
    }
  };

  const handleDelete = () => {
    if (idItem) {
      handleDeleteItem(
        DOCUMENTS_URL,
        setIsAlertOpen,
        idItem,
        addToast,
        setIsLoaded,
      );
    }
    closeModal();
  };

  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
  };

  useEffect(() => {
    if (idItem && selectedYear === null) {
      const publishedAt = watch("publishedAt");
      if (publishedAt) {
        setSelectedYear(dayjs(`${publishedAt}-01-01`, "YYYY-MM-DD"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchPermissionOptions = async (): Promise<Option[]> => {
    return [
      { id: 1, name: "Privado" },
      { id: 2, name: "Público" },
    ];
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={closeModal}
      title={title}
      isAlertOpen={isAlertOpen}
      setIsAlertOpen={setIsAlertOpen}
      handleDelete={handleDelete}
      alertText="Tem a certeza que pretende eliminar este documento?"
      alertTitle="Eliminar documento"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4} pb="2">
          <Box>
            <InputFieldComponent
              label="Nome"
              name="name"
              type="text"
              register={control.register}
              errors={errors}
              isRequired
            />
          </Box>
          <Box>
            <InputFieldComponent
              label="Autor"
              name="authorName"
              type="text"
              register={control.register}
              errors={errors}
              isRequired
            />
          </Box>
          <DatePickerComponent
            label="Ano de publicação"
            fieldName="publishedAt"
            selectedDate={selectedYear}
            onChange={(date: Dayjs | null) => {
              setSelectedYear(date);
            }}
            mode="year"
            format="YYYY"
            errors={errors}
            isRequired
            control={control}
            maxDate={dayjs().endOf("year")}
          />
          <CheckboxControlGroup
            name="subSystemOptions"
            control={control}
            errors={errors}
            fieldName="Área específica"
            fetchOptions={getSubSystem}
            isRequired
          />
          <CheckboxControlGroup
            name="provinceOptions"
            control={control}
            errors={errors}
            fieldName="Província"
            fetchOptions={getProvince}
            quantity={5}
          />
          <Flex direction="column">
            <LabelComponent
              title="Carregar documento"
              fontWeight="500"
              fontSize="md"
              isRequired
            />
            <FileUpload
              file={uploadedFile}
              setFile={setUploadedFile}
              onFileChange={handleFileChange}
              validExtensions={["pdf"]}
              uploadLimit={10}
              buttonName="Carregar (PDF) 10mb"
              shouldShowError={shouldShowFileError}
              loadingFile={loadingFile}
            />
          </Flex>
          <RadioControlGroup
            errors={errors}
            control={control}
            fetchOptions={fetchPermissionOptions}
            fieldName="Permissões de visualização do documento"
            name="permission"
            isRequired
          />
        </Stack>
        <ModalFooterComponent
          setIsAlertOpen={setIsAlertOpen}
          setIsOpen={closeModal}
          buttonTitle={buttonTitle}
          deleteButtonName="Eliminar documento"
          deleteEnabled={
            user?.permissions
              ? user.permissions.includes("DeleteDocument")
              : false
          }
          isLoaded={!loadingFile && isLoaded}
        />
      </form>
    </ModalComponent>
  );
};

export default ModalDocuments;
